import { getFileNameFromHeaders } from "modules/common";
import { EducationApplicationRequest, EducationsRequest } from "services";
import { createService } from "services/serviceFactory";
import { FarmHolderResponse } from ".";
import {
    AddRemoveEducationReminder,
    Education,
    EducationApplicationResponse,
    EducationsResponse,
    ExpertAreasResponse,
    MeasuresResponse,
    OperationTypesResponse,
    RegistryResponse,
    FarmerObligationsResponse,
} from "./response";

const dippService = createService("dipp");

async function getAllEducations(data: EducationsRequest, accessToken: string) {
    return dippService.post<EducationsResponse>("/dipp/v1/Education/GetEducations", data, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getAllMeasures(accessToken: string) {
    return dippService.get<MeasuresResponse>("/dipp/v1/Education/GetAllMeasures", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getAllOperationTypes(accessToken: string) {
    return dippService.get<OperationTypesResponse>("/dipp/v1/Education/GetAllOperationTypes", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getAllExpertAreas(accessToken: string) {
    return dippService.get<ExpertAreasResponse>("/dipp/v1/Education/GetAllExpertAreas", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function educationApplication(accessToken: string, body: EducationApplicationRequest) {
    return dippService.post<EducationApplicationResponse>(
        "dipp/v1/Education/EducationApplication",
        body,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function addRemoveEducationReminder(body: AddRemoveEducationReminder, accessToken: string) {
    return dippService.post("dipp/v1/Education/addRemoveEducationReminder", body, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getFarmMembers(accessToken: string, mibpg: string) {
    return dippService.get("/dipp/v1/Farmer/GetFarmMembers", {
        params: {
            mibpg,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getUserEducationsWithReminders(accessToken: string) {
    return dippService.get<EducationsResponse>("dipp/v1/Education/GetUserEducationsWithReminders", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getFarmHolder(accessToken: string, mibpg: string) {
    return dippService.get<FarmHolderResponse>("/dipp/v1/Farmer/GetFarmHolder", {
        params: {
            mibpg,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getEducation(educationId: string, accessToken: string) {
    return dippService.get<{ education: Education }>(
        `dipp/v1/Education/GetEducation/${educationId}`,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function downloadEducationCertificateRequest(
    accessToken: string,
    educationStatusUid: string
) {
    return await dippService.get("dipp/v1/Education/DownloadCertificate", {
        responseType: "blob",
        params: {
            statusUid: educationStatusUid,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function downloadEducationCertificate(accessToken: string, educationStatusUid: string) {
    const response = await downloadEducationCertificateRequest(accessToken, educationStatusUid);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    let filename = getFileNameFromHeaders(response) ?? `Potvrda_o_edukaciji.pdf`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
}

async function getRegistries(accessToken: string) {
    return dippService.get<{ registries: RegistryResponse[] }>("dipp/v1/Registry/GetRegistries", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getRegistriesByPgs(mibpg: string, accessToken: string) {
    return dippService.get(`/dipp/v1/Registry/GetRegistriesByPgs/${mibpg}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getFarmerObligations(farmerId: number[], accessToken: string) {
    return dippService.get<FarmerObligationsResponse>("/dipp/v1/Farmer/GetFarmerObligations", {
        params: {
            farmerId,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getEducationsByTitlesAndCounty(
    titles: string[],
    county: string,
    accessToken: string
) {
    return dippService.get<Record<string, string[]>>(
        "/dipp/v1/Education/GetEducationsByTitlesAndCounty",
        {
            params: {
                titles,
                county,
            },
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getEducationStatus(
    educationId: number,
    statusId: number,
    mibpg: string,
    accessToken: string
) {
    return dippService.get<any>("/dipp/v1/Education/GetEducationStatus", {
        params: {
            educationId,
            statusId,
            mibpg,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

export {
    addRemoveEducationReminder,
    downloadEducationCertificate,
    educationApplication,
    getAllEducations,
    getAllExpertAreas,
    getAllMeasures,
    getAllOperationTypes,
    getEducation,
    getFarmHolder,
    getFarmMembers,
    getRegistries,
    getRegistriesByPgs,
    getUserEducationsWithReminders,
    getFarmerObligations,
    getEducationsByTitlesAndCounty,
    getEducationStatus,
};
